import type { MapManifest } from '@/gameplay/types'
import { rectGridSlots } from './grid'

/** Крупный тайл: визуал + AABB контакта (user feedback). */
const TILE = 1.85
const COLS = 11
const ROWS = 8
/** Центр колонки 5 → originX = -5 * TILE */
const ORIGIN_X = -5 * TILE
/** r0 у юга поля */
const ORIGIN_Z = -6.3

const FIELD_HALF_X = (COLS / 2) * TILE
const FIELD_MIN_Z = ORIGIN_Z - TILE / 2
const FIELD_MAX_Z = ORIGIN_Z + (ROWS - 1) * TILE + TILE / 2
const FIELD_CENTER_Z = ORIGIN_Z + ((ROWS - 1) / 2) * TILE

/**
 * Открытый двор: юг spawn → поле 11×8 → северный проём Exit.
 * Боковые choke + solid props не дают обойти минное поле.
 * Spawn yaw = π: forward (−sin,−cos) смотрит на +Z к центроиду поля.
 */
export const courtyardMap: MapManifest = {
  id: 'courtyard',
  displayName: 'Периметр',
  description: 'Большой двор: зайди с юга, пройди поле 11×8, выйди в северный проём.',
  tileSize: TILE,
  slots: rectGridSlots(COLS, ROWS, ORIGIN_X, ORIGIN_Z, TILE),
  exitZone: { center: { x: 0, y: 0, z: FIELD_MAX_Z + 3.2 }, radius: 2.0 },
  spawn: { position: { x: 0, y: 1.7, z: FIELD_MIN_Z - 3.4 }, yaw: Math.PI },
  bounds: {
    minX: -FIELD_HALF_X - 6,
    maxX: FIELD_HALF_X + 6,
    minZ: FIELD_MIN_Z - 5,
    maxZ: FIELD_MAX_Z + 5,
  },
  fieldCentroid: { x: 0, y: 0, z: FIELD_CENTER_Z },
  minGrid: { cols: COLS, rows: ROWS },
  walls: [
    // Периметр-оболочка
    { x: 0, z: FIELD_MIN_Z - 4.6, w: FIELD_HALF_X * 2 + 12, d: 0.7, h: 3.2 },
    { x: -FIELD_HALF_X - 5.6, z: FIELD_CENTER_Z, w: 0.7, d: FIELD_MAX_Z - FIELD_MIN_Z + 12, h: 3.2 },
    { x: FIELD_HALF_X + 5.6, z: FIELD_CENTER_Z, w: 0.7, d: FIELD_MAX_Z - FIELD_MIN_Z + 12, h: 3.2 },
    // Северная стена с проёмом под Exit
    { x: -(FIELD_HALF_X + 2), z: FIELD_MAX_Z + 4.2, w: FIELD_HALF_X + 1, d: 0.7, h: 3.2 },
    { x: FIELD_HALF_X + 2, z: FIELD_MAX_Z + 4.2, w: FIELD_HALF_X + 1, d: 0.7, h: 3.2 },
    // Колонны проёма
    { x: -2.2, z: FIELD_MAX_Z + 2.4, w: 0.8, d: 0.8, h: 3.4 },
    { x: 2.2, z: FIELD_MAX_Z + 2.4, w: 0.8, d: 0.8, h: 3.4 },
    // Боковые choke вдоль поля — обход к Exit закрыт
    {
      x: -(FIELD_HALF_X + 3.4),
      z: FIELD_CENTER_Z,
      w: 5.6,
      d: ROWS * TILE + 2,
      h: 2.8,
    },
    {
      x: FIELD_HALF_X + 3.4,
      z: FIELD_CENTER_Z,
      w: 5.6,
      d: ROWS * TILE + 2,
      h: 2.8,
    },
    // Северные боковины к Exit: коридор |x|≲2.4
    { x: -(FIELD_HALF_X + 1.5), z: FIELD_MAX_Z + 1.6, w: FIELD_HALF_X + 0.5, d: 5.5, h: 2.9 },
    { x: FIELD_HALF_X + 1.5, z: FIELD_MAX_Z + 1.6, w: FIELD_HALF_X + 0.5, d: 5.5, h: 2.9 },
    // Низкие блок-препятствия у входа на поле (юг)
    { x: -6.2, z: FIELD_MIN_Z - 1.1, w: 3.4, d: 0.9, h: 1.5 },
    { x: 6.2, z: FIELD_MIN_Z - 1.1, w: 3.4, d: 0.9, h: 1.5 },
  ],
  props: [
    // Коробки у входа / углов — solid AABB
    { x: -7.5, z: FIELD_MIN_Z - 2.2, w: 1.4, d: 1.4, h: 1.4 },
    { x: 7.5, z: FIELD_MIN_Z - 2.2, w: 1.4, d: 1.4, h: 1.4 },
    { x: -FIELD_HALF_X - 1.2, z: FIELD_CENTER_Z - 2, w: 1.6, d: 1.6, h: 1.5 },
    { x: FIELD_HALF_X + 1.2, z: FIELD_CENTER_Z + 1.5, w: 1.5, d: 1.5, h: 1.6 },
    { x: -3.2, z: FIELD_MAX_Z + 2.8, w: 1.3, d: 1.3, h: 1.2 },
    { x: 3.2, z: FIELD_MAX_Z + 2.8, w: 1.3, d: 1.3, h: 1.2 },
  ],
  platforms: [
    {
      x: 0,
      z: FIELD_CENTER_Z,
      w: FIELD_HALF_X * 2 + 14,
      d: FIELD_MAX_Z - FIELD_MIN_Z + 14,
      y: 0,
    },
  ],
  // ~18–22% клеток (88 слотов)
  mineCountRange: { min: 14, max: 20 },
  defaultMode: 'exit',
}
